import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { cn } from '~/lib/cn';
import { getArtistContactInfo } from '~/modules/artist-roster/lib/artists';

import { ContactCards } from './components/ContactCards';
import { ContactsSkeleton } from './components/ContactsSkeleton';

interface ArtistContactInfoProps {
  partyId: number;
}

type InfoType = 'artist' | 'songwriter';

export function ArtistContactInfo({ partyId }: ArtistContactInfoProps) {
  const { t } = useTranslation('roster');

  const { data, isLoading } = useQuery({
    queryKey: ['artist-contact-info', partyId],
    queryFn: () => getArtistContactInfo({ partyId }),
  });

  const [selectedInfoType, setSelectedInfoType] = useState<InfoType>('artist');
  const artistContacts = data?.artistContacts;
  const songwriterContacts = data?.songwriterContacts;
  const contacts =
    selectedInfoType === 'artist' || !songwriterContacts
      ? artistContacts
      : songwriterContacts;

  if (isLoading) {
    return (
      <div className="flex w-[600px] flex-col gap-lg rounded-sm border p-md bg-level-03 border-emphasis-strong text-white">
        <ContactsSkeleton />
      </div>
    );
  }

  if (!artistContacts?.length && !songwriterContacts?.length) {
    return (
      <div className="flex w-[600px] flex-col gap-lg rounded-sm border p-md bg-level-03 border-emphasis-strong text-white">
        <div className="flex flex-col items-center justify-center gap-xl rounded-sm py-5xl">
          <span className="flex shrink-0 items-center justify-center">
            <Icon
              type="user-large-slash"
              size="2xl"
              variant="light"
              aria-hidden
              className="text-default"
            />
          </span>
          <span className="text-body-md font-bold text-strong">
            {t('contactInfo.noData')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-[600px] flex-col gap-lg rounded-sm border p-md bg-level-03 border-emphasis-strong text-white">
      {songwriterContacts?.length ? (
        <div className="flex w-full items-center justify-start gap-md pb-xs bg-transparent text-default">
          <button
            disabled={isLoading}
            className={cn('underline-offset-8 hover:underline', {
              'underline decoration-2 decoration-primary-default':
                selectedInfoType === 'artist',
            })}
            onClick={() => setSelectedInfoType('artist')}
          >
            {t('contactInfo.artistInfo')}
          </button>
          <button
            disabled={isLoading}
            className={cn('underline-offset-8 hover:underline', {
              'underline decoration-2 decoration-primary-default':
                selectedInfoType === 'songwriter',
            })}
            onClick={() => setSelectedInfoType('songwriter')}
          >
            {t('contactInfo.songwriterInfo')}
          </button>
        </div>
      ) : null}
      <ContactCards contacts={contacts} />
    </div>
  );
}

ArtistContactInfo.displayName = 'ArtistContactInfo';
